import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Scale, FileSearch, FlaskConical, DollarSign, GitCompareArrows,
  Package, Bell, ArrowLeft, TrendingUp, TrendingDown, Shield
} from "lucide-react";

const GradeColors = { A: "text-emerald-400", B: "text-green-400", C: "text-yellow-400", D: "text-orange-400", F: "text-red-400" };
const SeverityColors = { CRITICAL: "bg-red-500/20 text-red-300", HIGH: "bg-orange-500/20 text-orange-300", MEDIUM: "bg-yellow-500/20 text-yellow-300", LOW: "bg-slate-500/20 text-slate-300" };

function Section({ title, children }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4">
      <h3 className="text-white font-medium mb-3">{title}</h3>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, color = "text-white" }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-slate-400 text-xs mt-1">{label}</div>
      {sub && <div className="text-slate-500 text-xs">{sub}</div>}
    </div>
  );
}

function BalanceSheetTab() {
  const [agentId, setAgentId] = useState("");
  const [sheet, setSheet] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSheet = async () => {
    if (!agentId) return toast.error("Enter an agent ID");
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.getBalanceSheet(agentId),
        api.getDebtTrend(agentId),
      ]);
      setSheet(s);
      setTrend(t);
      toast.success("Balance sheet computed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Intelligence Balance Sheet">
        <div className="flex gap-2 mb-4">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
          <Button onClick={fetchSheet} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Computing..." : "Compute"}</Button>
        </div>
        {sheet && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <StatCard label="Intelligence Yield" value={`${(sheet.yield?.yield_score * 100 || 0).toFixed(1)}%`} color={GradeColors[sheet.grade] || "text-white"} />
              <StatCard label="Grade" value={sheet.grade} color={GradeColors[sheet.grade]} />
              <StatCard label="Assets" value={sheet.assets?.count || 0} sub={`${sheet.assets?.expired || 0} expired`} color="text-emerald-400" />
              <StatCard label="Liabilities" value={sheet.liabilities?.count || 0} sub={`${sheet.liabilities?.expired || 0} expired`} color="text-red-400" />
              <StatCard label="Debt Interest" value={sheet.interest?.total_interest?.toFixed(2) || "0"} color="text-orange-400" />
            </div>
            {sheet.yield?.sub_scores && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {Object.entries(sheet.yield.sub_scores).map(([k, v]) => (
                  <div key={k} className="bg-slate-900 rounded p-2">
                    <span className="text-slate-400 text-xs">{k.replace(/_/g, " ")}</span>
                    <span className="text-white text-sm ml-2">{typeof v === "number" ? v.toFixed(3) : v}</span>
                  </div>
                ))}
              </div>
            )}
            {sheet.interest?.by_category && (
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(sheet.interest.by_category).map(([k, v]) => (
                  <div key={k} className="bg-slate-900 rounded p-2 text-center">
                    <div className="text-orange-400 text-sm font-mono">{v.toFixed(2)}</div>
                    <div className="text-slate-500 text-xs">{k.replace(/_/g, " ")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
      {trend && (
        <Section title="Debt Trend">
          <div className="flex items-center gap-3">
            {trend.trend === "improving" ? <TrendingUp className="w-5 h-5 text-emerald-400" /> : <TrendingDown className="w-5 h-5 text-red-400" />}
            <span className="text-white font-medium capitalize">{trend.trend}</span>
            <span className="text-slate-400 text-sm">slope: {trend.slope?.toFixed(4)}</span>
            <span className="text-slate-500 text-sm">{trend.data_points} data points</span>
          </div>
        </Section>
      )}
    </div>
  );
}

function ContextQualityTab() {
  const [docText, setDocText] = useState("");
  const [sourceType, setSourceType] = useState("verified_doc");
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);

  const scoreDoc = async () => {
    if (!docText) return toast.error("Enter document text");
    setLoading(true);
    try {
      const result = await api.scoreContextDocument({
        doc_text: docText,
        metadata: { source_type: sourceType, last_updated: new Date().toISOString(), verification_status: "verified" },
      });
      setScore(result);
      toast.success(`Quality score: ${result.quality_score}`);
    } catch (e) { toast.error("Failed to score document"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Score Context Document">
        <div className="space-y-3">
          <Textarea placeholder="Paste document text..." value={docText} onChange={e => setDocText(e.target.value)} rows={4} className="bg-slate-900 border-slate-600 text-white" />
          <div className="flex gap-2 items-center">
            <select value={sourceType} onChange={e => setSourceType(e.target.value)} className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm">
              <option value="database_schema">Database Schema</option>
              <option value="api_spec">API Spec</option>
              <option value="verified_doc">Verified Doc</option>
              <option value="extracted_doc">Extracted Doc</option>
              <option value="user_upload">User Upload</option>
              <option value="unknown">Unknown</option>
            </select>
            <Button onClick={scoreDoc} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Scoring..." : "Score"}</Button>
          </div>
        </div>
        {score && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label="Quality Score" value={score.quality_score} color={score.quality_score >= 70 ? "text-emerald-400" : score.quality_score >= 40 ? "text-yellow-400" : "text-red-400"} />
            <StatCard label="Freshness" value={score.dimensions?.freshness?.toFixed(2)} />
            <StatCard label="Authority" value={score.dimensions?.authority?.toFixed(2)} />
            <StatCard label="Completeness" value={score.dimensions?.completeness?.toFixed(2)} />
            <StatCard label="Expired" value={score.expired ? "Yes" : "No"} color={score.expired ? "text-red-400" : "text-emerald-400"} />
          </div>
        )}
      </Section>
    </div>
  );
}

function EvaluationsTab() {
  const [suiteName, setSuiteName] = useState("");
  const [workflowType, setWorkflowType] = useState("classification");
  const [casesJson, setCasesJson] = useState('[{"name":"test1","input_data":"input","expected":"expected","eval_type":"exact_match","severity":"medium"}]');
  const [suite, setSuite] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [outputs, setOutputs] = useState("");
  const [loading, setLoading] = useState(false);

  const createSuite = async () => {
    if (!suiteName) return toast.error("Enter suite name");
    setLoading(true);
    try {
      const cases = JSON.parse(casesJson);
      const result = await api.createEvalSuite({ suite_name: suiteName, workflow_type: workflowType, test_cases: cases });
      setSuite(result);
      toast.success(`Suite created: ${result.suite_id}`);
    } catch (e) { toast.error(e.message || "Failed"); }
    setLoading(false);
  };

  const runSuite = async () => {
    if (!suite) return toast.error("Create a suite first");
    setLoading(true);
    try {
      const outputList = outputs.split("\n").filter(Boolean);
      const result = await api.runEvalSuite(suite.suite_id, outputList);
      setRunResult(result);
      toast.success(`Grade: ${result.grade} (${(result.pass_rate * 100).toFixed(0)}% pass)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Create Evaluation Suite">
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Suite name" value={suiteName} onChange={e => setSuiteName(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            <select value={workflowType} onChange={e => setWorkflowType(e.target.value)} className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm">
              {["classification","extraction","generation","decision","reconciliation","code_change","triage"].map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <Textarea placeholder="Test cases JSON array" value={casesJson} onChange={e => setCasesJson(e.target.value)} rows={4} className="bg-slate-900 border-slate-600 text-white font-mono text-xs" />
          <Button onClick={createSuite} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">Create Suite</Button>
        </div>
        {suite && (
          <div className="mt-3 bg-slate-900 rounded p-3">
            <span className="text-emerald-400 text-sm">Suite: {suite.suite_id}</span>
            <span className="text-slate-400 text-sm ml-3">{suite.case_count} test cases</span>
          </div>
        )}
      </Section>
      {suite && (
        <Section title="Run Evaluation">
          <Textarea placeholder="One output per line (must match test case count)" value={outputs} onChange={e => setOutputs(e.target.value)} rows={4} className="bg-slate-900 border-slate-600 text-white" />
          <Button onClick={runSuite} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 mt-2">Run Suite</Button>
          {runResult && (
            <div className="mt-4">
              <div className="grid grid-cols-4 gap-3 mb-3">
                <StatCard label="Grade" value={runResult.grade} color={GradeColors[runResult.grade]} />
                <StatCard label="Pass Rate" value={`${(runResult.pass_rate * 100).toFixed(0)}%`} />
                <StatCard label="Passed" value={runResult.passed_cases} color="text-emerald-400" />
                <StatCard label="Failed" value={runResult.failed_cases} color="text-red-400" />
              </div>
              {runResult.results && (
                <div className="space-y-1">
                  {runResult.results.map((r, i) => (
                    <div key={i} className={`flex items-center gap-2 px-3 py-1 rounded text-sm ${r.passed ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
                      <span className={r.passed ? "text-emerald-400" : "text-red-400"}>{r.passed ? "PASS" : "FAIL"}</span>
                      <span className="text-white">{r.name}</span>
                      <span className="text-slate-400 ml-auto">{r.eval_type} | {r.details}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function FinOpsTab() {
  const [agentId, setAgentId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    if (!agentId) return toast.error("Enter an agent ID");
    setLoading(true);
    try {
      const result = await api.getFinOpsReport(agentId);
      setReport(result);
      toast.success("FinOps report generated");
    } catch (e) { toast.error("Failed to fetch report"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Intelligence FinOps Report">
        <div className="flex gap-2 mb-4">
          <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
          <Button onClick={fetchReport} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Loading..." : "Get Report"}</Button>
        </div>
        {report && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <StatCard label="Transactions" value={report.summary?.total_transactions || 0} />
              <StatCard label="Total Cost" value={`$${report.summary?.total_cost_usd?.toFixed(4) || "0"}`} color="text-yellow-400" />
              <StatCard label="Acceptance Rate" value={`${((report.summary?.acceptance_rate || 0) * 100).toFixed(0)}%`} color="text-emerald-400" />
              <StatCard label="Waste Ratio" value={`${((report.waste?.waste_ratio || 0) * 100).toFixed(0)}%`} color={report.waste?.waste_ratio > 0.3 ? "text-red-400" : "text-emerald-400"} />
              <StatCard label="Rework Hours" value={report.rework?.rework_hours?.toFixed(1) || "0"} color="text-orange-400" />
            </div>
            {report.recommendations?.length > 0 && (
              <div className="space-y-2 mt-3">
                <h4 className="text-slate-300 text-sm font-medium">Recommendations</h4>
                {report.recommendations.map((r, i) => (
                  <div key={i} className="bg-indigo-500/10 border border-indigo-500/30 rounded px-3 py-2 text-sm">
                    <span className="text-indigo-300 font-medium">{r.type}: </span>
                    <span className="text-slate-300">{r.detail}</span>
                    {r.estimated_savings_pct > 0 && <span className="text-emerald-400 ml-2">~{r.estimated_savings_pct}% savings</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

function DriftTab() {
  const [agentId, setAgentId] = useState("");
  const [report, setReport] = useState(null);
  const [baselineType, setBaselineType] = useState("model");
  const [metricsJson, setMetricsJson] = useState('{"model_name":"gpt-4o","quality_score":0.9}');
  const [loading, setLoading] = useState(false);

  const recordBl = async () => {
    if (!agentId) return toast.error("Enter an agent ID");
    setLoading(true);
    try {
      const metrics = JSON.parse(metricsJson);
      await api.recordBaseline(agentId, { baseline_type: baselineType, metrics });
      toast.success("Baseline recorded");
    } catch (e) { toast.error(e.message || "Failed"); }
    setLoading(false);
  };

  const checkDrift = async () => {
    if (!agentId) return toast.error("Enter an agent ID");
    setLoading(true);
    try {
      const result = await api.checkDrift(agentId);
      setReport(result);
      toast.success(`Drift check: ${result.drift_detected_count} issues`);
    } catch (e) { toast.error("Failed to check drift"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Establish Baseline">
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            <select value={baselineType} onChange={e => setBaselineType(e.target.value)} className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm">
              {["model","prompt","config","cost","quality"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <Textarea placeholder='Metrics JSON, e.g. {"model_name":"gpt-4o","quality_score":0.9}' value={metricsJson} onChange={e => setMetricsJson(e.target.value)} rows={2} className="bg-slate-900 border-slate-600 text-white font-mono text-xs" />
          <div className="flex gap-2">
            <Button onClick={recordBl} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">Record Baseline</Button>
            <Button onClick={checkDrift} disabled={loading} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">Check Drift</Button>
          </div>
        </div>
      </Section>
      {report && (
        <Section title="Drift Report">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatCard label="Issues Found" value={report.drift_detected_count} color={report.drift_detected_count > 0 ? "text-red-400" : "text-emerald-400"} />
            <StatCard label="Total Checks" value={report.total_checks} />
            <StatCard label="Overall Severity" value={report.overall_severity} color={report.overall_severity === "high" ? "text-red-400" : report.overall_severity === "medium" ? "text-yellow-400" : "text-emerald-400"} />
          </div>
          {report.by_type && Object.entries(report.by_type).map(([type, info]) => (
            <div key={type} className={`flex items-center gap-3 px-3 py-2 rounded mb-1 ${info.detected ? "bg-red-500/10" : "bg-slate-900"}`}>
              <span className={`w-2 h-2 rounded-full ${info.detected ? "bg-red-400" : "bg-emerald-400"}`} />
              <span className="text-white text-sm">{type}</span>
              <span className="text-slate-400 text-xs ml-auto">{info.severity}</span>
            </div>
          ))}
          {report.suggestions?.length > 0 && (
            <div className="mt-3 space-y-1">
              {report.suggestions.map((s, i) => (
                <div key={i} className="bg-yellow-500/10 border border-yellow-500/30 rounded px-3 py-2 text-sm text-yellow-200">{s}</div>
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}

function EvidenceTab() {
  const [agentId, setAgentId] = useState("");
  const [task, setTask] = useState("");
  const [purpose, setPurpose] = useState("");
  const [required, setRequired] = useState("");
  const [forbidden, setForbidden] = useState("");
  const [docsJson, setDocsJson] = useState('[{"name":"contract_2026","text":"Fuel surcharge is 12%","metadata":{"confidence":0.9,"valid_to":"2026-12-31T00:00:00+00:00"}}]');
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);

  const compile = async () => {
    if (!agentId || !task || !purpose) return toast.error("Fill in all fields");
    setLoading(true);
    try {
      const docs = JSON.parse(docsJson);
      const result = await api.compileEvidence({
        agent_id: agentId, task, purpose,
        required_sources: required ? required.split(",").map(s => s.trim()) : [],
        forbidden_sources: forbidden ? forbidden.split(",").map(s => s.trim()) : [],
        documents: docs,
      });
      setBundle(result);
      toast.success(`Bundle compiled: ${result.included_count} included, ${result.excluded_count} excluded`);
    } catch (e) { toast.error(e.message || "Failed"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="Compile Evidence Bundle">
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Agent ID" value={agentId} onChange={e => setAgentId(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            <Input placeholder="Task (e.g. reconcile invoice)" value={task} onChange={e => setTask(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            <Input placeholder="Purpose (e.g. invoice_reconciliation)" value={purpose} onChange={e => setPurpose(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Required sources (comma-separated)" value={required} onChange={e => setRequired(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
            <Input placeholder="Forbidden sources (comma-separated)" value={forbidden} onChange={e => setForbidden(e.target.value)} className="bg-slate-900 border-slate-600 text-white" />
          </div>
          <Textarea placeholder="Documents JSON array" value={docsJson} onChange={e => setDocsJson(e.target.value)} rows={3} className="bg-slate-900 border-slate-600 text-white font-mono text-xs" />
          <Button onClick={compile} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Compiling..." : "Compile Bundle"}</Button>
        </div>
      </Section>
      {bundle && (
        <>
          <Section title="Evidence Bundle">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <StatCard label="Included" value={bundle.included_count} color="text-emerald-400" />
              <StatCard label="Excluded" value={bundle.excluded_count} color="text-red-400" />
              <StatCard label="Sufficiency" value={bundle.sufficiency?.status || "unknown"} color={bundle.sufficiency?.status === "sufficient" ? "text-emerald-400" : "text-yellow-400"} />
            </div>
            {bundle.included?.length > 0 && (
              <div className="mb-3">
                <h4 className="text-slate-300 text-sm mb-2">Included Sources</h4>
                {bundle.included.map((d, i) => (
                  <div key={i} className="bg-emerald-500/10 rounded px-3 py-1 mb-1 text-sm flex items-center gap-2">
                    <span className="text-emerald-300">{d.name}</span>
                    <span className="text-slate-400 ml-auto">confidence: {d.confidence}</span>
                  </div>
                ))}
              </div>
            )}
            {bundle.excluded?.length > 0 && (
              <div>
                <h4 className="text-slate-300 text-sm mb-2">Excluded Sources</h4>
                {bundle.excluded.map((d, i) => (
                  <div key={i} className="bg-red-500/10 rounded px-3 py-1 mb-1 text-sm flex items-center gap-2">
                    <span className="text-red-300">{d.name}</span>
                    <span className="text-slate-400 ml-auto">{d.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
          {bundle.manifest && (
            <Section title="Evidence Manifest">
              <div className="text-slate-300 text-sm">
                Estimated tokens: <span className="text-indigo-400 font-mono">{bundle.manifest.estimated_tokens}</span>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function EventsTab() {
  const [windowHours, setWindowHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const result = await api.getEventSummary(windowHours);
      setSummary(result);
      toast.success(`${result.total_events} events in last ${windowHours}h`);
    } catch (e) { toast.error("Failed to fetch events"); }
    setLoading(false);
  };

  return (
    <div>
      <Section title="AI Safety Event Summary">
        <div className="flex gap-2 mb-4">
          <select value={windowHours} onChange={e => setWindowHours(Number(e.target.value))} className="bg-slate-900 border border-slate-600 text-white rounded px-3 py-2 text-sm">
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
          </select>
          <Button onClick={fetchSummary} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">{loading ? "Loading..." : "Fetch Events"}</Button>
        </div>
        {summary && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <StatCard label="Total Events" value={summary.total_events} />
              <StatCard label="Critical" value={summary.by_severity?.CRITICAL || 0} color="text-red-400" />
              <StatCard label="High" value={summary.by_severity?.HIGH || 0} color="text-orange-400" />
              <StatCard label="Medium" value={summary.by_severity?.MEDIUM || 0} color="text-yellow-400" />
            </div>
            {summary.by_type && Object.keys(summary.by_type).length > 0 && (
              <div className="mb-4">
                <h4 className="text-slate-300 text-sm mb-2">By Event Type</h4>
                {Object.entries(summary.by_type).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                  <div key={type} className="flex items-center gap-2 px-3 py-1 bg-slate-900 rounded mb-1">
                    <span className="text-white text-sm">{type}</span>
                    <span className="text-indigo-400 font-mono ml-auto">{count}</span>
                  </div>
                ))}
              </div>
            )}
            {summary.top_agents?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-slate-300 text-sm mb-2">Top Agents by Event Volume</h4>
                {summary.top_agents.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1 bg-slate-900 rounded mb-1">
                    <span className="text-white text-sm">{a.agent_id}</span>
                    <span className="text-yellow-400 font-mono ml-auto">{a.event_count} events</span>
                  </div>
                ))}
              </div>
            )}
            {summary.escalation_candidates?.length > 0 && (
              <div>
                <h4 className="text-red-300 text-sm mb-2">Escalation Required</h4>
                {summary.escalation_candidates.map((e, i) => (
                  <div key={i} className="bg-red-500/10 border border-red-500/30 rounded px-3 py-2 mb-1 text-sm">
                    <span className="text-red-300 font-medium">{e.action}: </span>
                    <span className="text-slate-300">{e.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

export default function AIDebtPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet><title>AI Debt Control Tower | AgentCheck</title></Helmet>
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/agents" className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2"><Scale className="w-5 h-5 text-indigo-400" />AI Debt Control Tower</h1>
              <p className="text-slate-400 text-sm">Intelligence Balance Sheet | Graph-first, Evidence-second, LLM-last</p>
            </div>
          </div>
          <nav className="flex items-center gap-4">
            <Link to="/agents" className="text-slate-400 hover:text-white transition-colors text-sm">Agents</Link>
            <Link to="/agents/ai-doctor" className="text-slate-400 hover:text-white transition-colors text-sm">AI Doctor</Link>
            <Link to="/docs" className="text-slate-400 hover:text-white transition-colors text-sm">Docs</Link>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="balance-sheet">
          <TabsList className="bg-slate-900 border border-slate-800 mb-6">
            <TabsTrigger value="balance-sheet" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><Scale className="w-4 h-4 mr-1" />Balance Sheet</TabsTrigger>
            <TabsTrigger value="context" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><FileSearch className="w-4 h-4 mr-1" />Context Quality</TabsTrigger>
            <TabsTrigger value="evaluations" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><FlaskConical className="w-4 h-4 mr-1" />Evaluations</TabsTrigger>
            <TabsTrigger value="finops" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><DollarSign className="w-4 h-4 mr-1" />FinOps</TabsTrigger>
            <TabsTrigger value="drift" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><GitCompareArrows className="w-4 h-4 mr-1" />Drift</TabsTrigger>
            <TabsTrigger value="evidence" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><Package className="w-4 h-4 mr-1" />Evidence</TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white text-slate-400"><Bell className="w-4 h-4 mr-1" />Events</TabsTrigger>
          </TabsList>
          <TabsContent value="balance-sheet"><BalanceSheetTab /></TabsContent>
          <TabsContent value="context"><ContextQualityTab /></TabsContent>
          <TabsContent value="evaluations"><EvaluationsTab /></TabsContent>
          <TabsContent value="finops"><FinOpsTab /></TabsContent>
          <TabsContent value="drift"><DriftTab /></TabsContent>
          <TabsContent value="evidence"><EvidenceTab /></TabsContent>
          <TabsContent value="events"><EventsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
